import React, { useEffect, useState, useCallback, useMemo, useRef, Suspense } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';
import { kebabCase } from 'lodash';

import { BBCommonProps, BBStates } from 'types/story';
import { IFRAME_ACTIONS, transmitTo } from 'common/utils/iframe-tunnel';
import CheckIcon from 'common/components/Icon/CheckMark';
import ChangeIcon from 'common/components/Icon/Change';
import EditIcon from 'common/components/Icon/Edit';
import { parseImgSrc, getOptimizedSrc, isGif, isSvg, isImgix, BG_PARAMS } from 'common/utils/assets';
import { COMPONENT_STATES, CSS_PROPS, IMG_TINT_ATTR, UNIFORM_PROPS } from 'common/constants';
import { isLayerType } from 'common/utils/is-layer-type';
import { useIsSelected } from 'client/reducers/editor/selectors';
import { transmitToAdminBbUpdate } from 'client/utils/transmit-to-admin-bb-update';
import withCardTransitionContext from 'client/components/common/BuildingBlocks/BuildingBlockEnhancer';
import { getLinkProps } from 'client/components/common/BuildingBlocks/utils/common';
import { EventProviderContextT, withEventProvider } from 'client/components/pages/Story/EventProvider/Context';
import { SelectionToolbarPortal } from 'client/components/common/SelectionHint/Toolbar';

import ImageLoadable from './ImageLoadable';
import css from './Img.scss';

const ImageEditor = React.lazy(() => import('client/components/common/BuildingBlocks/Img/ImageEditor'));

type Props = BBCommonProps & EventProviderContextT;

const Img = (props: Props) => {
	// props
	const { editableModeProps: EMP, stateAttrs, currentMediaQuery } = props;
	const { nodeProps } = props.uiConfig;
	const isStoryElement = isLayerType(props).global;
	const { Component, ...componentProps } = getComponent(props);
	const nodeID = props.uiConfig.nodeProps.id;

	// image
	const { styles } = props.uiConfig.componentProps;
	const isHidden = styles.display === 'none';
	const bgImgProp = styles.backgroundImage ?? '';
	const bgSizeProp = styles.backgroundSize ?? '';
	const bgPositionProp = styles.backgroundPosition ?? '';
	const bgImgParsed = useMemo(
		() =>
			// transform all background properties into single src string value to work with src only
			parseImgSrc(bgImgProp, {
				backgroundSize: bgSizeProp,
				backgroundPosition: bgPositionProp,
			}),
		[bgImgProp, bgSizeProp, bgPositionProp]
	);
	const src = bgImgParsed.href;

	// state
	const isSelected = useSelector(useIsSelected(nodeID));
	const [isEdit, setIsEdit] = useState(false);
	const [, setIsMounted] = useState(false);

	const srcEditable = useRef('');
	const setSrcEditable = useCallback((val: string) => {
		srcEditable.current = val;
	}, []);

	const save = useCallback(
		(value: string) => {
			const np = EMP?.nodeProps ?? {};
			const path = np['data-path'];
			let state: BBStates = COMPONENT_STATES.DEFAULT;
			Object.keys(COMPONENT_STATES).forEach(key => {
				if (np[`data-${kebabCase(COMPONENT_STATES[key])}`] !== undefined) {
					state = COMPONENT_STATES[key];
				}
			});

			const urlValue = new URL(value);
			const basePath = `elements.${path}.uiConfig.componentProps.styles.${state}.${currentMediaQuery}`;
			const updatesMap = new Map<string, string>();

			// read edited param from src and store as separated css property ("background-size")
			const nextBgSize = urlValue.searchParams.get(BG_PARAMS.fsz)!;
			if (nextBgSize !== bgImgParsed.fsz) {
				updatesMap.set(`${basePath}.${CSS_PROPS.background.backgroundSize}`, nextBgSize);
			}

			// read edited param from src and store as separated css property ("background-position")
			const nextBgPos = urlValue.searchParams.get(BG_PARAMS.rect)!;
			if (nextBgPos !== bgImgParsed.rect) {
				updatesMap.set(`${basePath}.${CSS_PROPS.background.backgroundPosition}`, nextBgPos);
			}

			transmitToAdminBbUpdate({
				id: Img.name,
				path: [...updatesMap.keys()],
				value: [...updatesMap.values()],
				isStory: isStoryElement,
			});
		},
		[EMP?.nodeProps, isStoryElement, currentMediaQuery, bgImgParsed]
	);

	// Close edit mode on bg image change/update
	useEffect(() => {
		setIsEdit(false);
	}, [bgImgProp, bgSizeProp, bgPositionProp, currentMediaQuery]);

	// re-render once to get a bbox
	useEffect(() => {
		setIsMounted(true);
	}, []);

	// Save on selection change
	const isChanged = srcEditable.current && srcEditable.current !== bgImgParsed.href;
	useEffect(() => {
		if (isEdit && !isSelected) {
			setIsEdit(false);

			if (isChanged) save(srcEditable.current);
		}
	}, [isSelected, isEdit, save, isChanged]);

	const el = props.containerRef?.current;
	let bbox: Pick<DOMRect, 'top' | 'left' | 'width' | 'height'> | null = null;
	if (el) {
		const { top, left } = el.getBoundingClientRect();
		bbox = { top, left, width: el.offsetWidth, height: el.offsetHeight };
		// get offsetWidth/Height instead of getBoundingClientRect for not take in account scale transformations
	}

	const handleSave = () => {
		setIsEdit(v => {
			if (v && srcEditable.current && srcEditable.current !== bgImgParsed.href) {
				save(srcEditable.current);
				return v;
			}
			return !v;
		});
	};

	// Event listeners
	const externalLink = props.uiConfig.componentProps[UNIFORM_PROPS.btnLink];
	const internalLink = externalLink ? '' : props.uiConfig.componentProps[UNIFORM_PROPS.btnNavLink];
	const eventListeners = props.isEditableMode
		? {}
		: { onClick: () => internalLink && props.eventProvider.events.flow(internalLink, { isDirectId: true }) };

	const isNonEditableFormat = isGif(src) || isSvg(src) || !isImgix(src);
	const className = cn(css.img, nodeProps.className, EMP?.nodeProps?.className, {
		[css.cover]: bgImgParsed.fsz === 'cover',
		[css.contain]: bgImgParsed.fsz === 'contain',
		[css.defaultCover]: !bgImgParsed.rect || isNonEditableFormat,
		[css.link]: externalLink || internalLink,
	});

	// is reused in other building block to achieve image functionality
	const isCustom = props.custom !== undefined;

	return (
		<Component
			{...(isCustom ? null : componentProps)}
			{...(isCustom ? null : nodeProps)}
			{...(isCustom ? null : stateAttrs)}
			{...(isCustom ? null : props.eventListeners)}
			{...(isCustom ? null : eventListeners)}
			{...(isCustom ? null : EMP?.nodeProps)}
			onDoubleClick={e => {
				if (!props.isEditableMode || e.target.nodeName === 'INPUT' || isNonEditableFormat) return;
				handleSave();
			}}
			style={isCustom ? props.custom?.style : nodeProps?.style}
			className={className}
			ref={props.containerRef}
		>
			{/* Placeholder */}
			{!src && <div className={css.empty}>No image</div>}

			{/* Image */}
			{src && !isEdit && bbox && !isHidden && (
				<ImageLoadable
					src={getOptimizedSrc({ src, width: bbox.width })}
					alt={props.uiConfig.componentProps[UNIFORM_PROPS.alt]}
				/>
			)}

			{/* Tint */}
			{!isEdit && <span {...{ [IMG_TINT_ATTR]: '' }} />}

			{/* Editor */}
			{src && isEdit && bbox && (
				<Suspense fallback={null}>
					<ImageEditor src={src} setEditableSrc={setSrcEditable} bbox={bbox} nodeID={nodeID} />
				</Suspense>
			)}

			{/* Toolbar */}
			{props.isEditableMode && !isHidden && (
				<SelectionToolbarPortal nodeID={nodeID}>
					{!isEdit && (
						<button
							title="Replace image source"
							type="button"
							onClick={() => {
								// Flow: send msg to admin -> read msg -> emit inspector event -> call event listener ->
								// -> call image upload
								transmitTo({
									id: 'Image',
									target: 'admin',
									action: IFRAME_ACTIONS.UPLOAD_IMAGE,
								});
							}}
						>
							<ChangeIcon width={11} />
						</button>
					)}
					{!isNonEditableFormat && (
						<button
							type="button"
							onClick={handleSave}
							title={isEdit ? 'Apply changes' : 'Edit image'}
							data-color={isEdit ? 'brand' : ''}
						>
							{isEdit ? <CheckIcon width={13} /> : <EditIcon width={14} />}
						</button>
					)}
				</SelectionToolbarPortal>
			)}
		</Component>
	);
};

function getComponent(props: Props): { Component: any; href?: any; target?: string; rel?: string | undefined } {
	const externalLink = props.uiConfig.componentProps[UNIFORM_PROPS.btnLink];

	if (externalLink && !props.isEditableMode) {
		return getLinkProps(props, { link: externalLink });
	}

	return {
		Component: 'div',
	};
}

export default withEventProvider(withCardTransitionContext(Img));
